import React, { useCallback, useEffect, useRef } from 'react';

import { Box, Grid, Typography } from '@mui/material';

import {
  BookingState,
  CalendarStateKey,
  getActiveWebView,
  getTypeOfUser,
  PaymentStatus,
  PharmacistLegendVariant,
  PharmacyLegendIntermediate,
  PharmacyLegendVariant,
  TypeOfUser,
} from '@pharmaplan/common';

import { ReactComponent as BellIcon } from '../../../../assets/svg/calendarBellIcon.svg';
import useCustomMonthEvent from '../../../../hooks/useCustomMonthEvent';
import useMultipleDelete from '../../../../hooks/useMultipleDelete';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import Watchlisted from '../../../common/Watchlist/Watchlisted';
import ThemeConstants from '../../../../theme/ThemeConstants';
import genericClasses from '../../../../theme/GenericClasses';
import ICalendarEvent from '../../../../types/ICalendar';
import WavingHand from '../../../common/WavingHand';
import strings from '../../../../localization';

import ProfessionalIconList from './ProfessionalIconList';
import CalendarTooltip from '../CalendarTooltip';
import classes from './style';
import StackedBox from '../../../common/StackedBox';
import { stackedChipsColors, stackedSmallChipsColors } from '../../../../helpers/Constants';

interface ICustomEventProps {
  event: ICalendarEvent;
  style: (React.CSSProperties & { xOffset: number }) | undefined;
  onSelect: (event: ICalendarEvent) => void;
  eventIds: Array<string>;
  isAutoSyncIDMap?: Record<string, boolean>;
}

const CustomMonthEvent = ({
  event,
  style,
  eventIds,
  onSelect,
  isAutoSyncIDMap,
}: ICustomEventProps) => {
  const { stopMouseDown } = useMultipleDelete();
  const ref = useRef<HTMLElement | null>(null);

  const typeOfUser = useAppSelector(getTypeOfUser).toLowerCase() as TypeOfUser;
  const activeWebView = useAppSelector(getActiveWebView);

  const {
    start,
    end,
    title,
    type: eventType,
    id,
    code,
    count,
    bookingState,
    durations,
    replacementTypes,
    isMatchingPrev,
    isDeleted,
    isInWatchlistPrev,
    isLongTerm,
    paymentStatus,
  } = event ?? {};

  const isPending = (eventType === PharmacyLegendVariant.requested
      && paymentStatus === PaymentStatus.Initiated)
    || paymentStatus === PaymentStatus.Processing;

  const type = isPending ? PharmacyLegendIntermediate.pending : eventType;

  const isPharmacy = typeOfUser === TypeOfUser.pharmacy.toLowerCase();
  const { handlePress, handleTooltipClick } = useCustomMonthEvent({
    event,
    eventIds,
    isAutoSyncIDMap,
  });

  const isRequestedBooked = type === PharmacistLegendVariant.booked
    || type === PharmacistLegendVariant.requested;
  const isPharmacist = typeOfUser === TypeOfUser.pharmacist.toLowerCase();
  const requiresAction = bookingState === BookingState.PendingWithPharmacist
    || bookingState === BookingState.UpdatedTiming;

  const showBellIcon = isRequestedBooked && isPharmacist && requiresAction;
  const showReplacementType = isPharmacy;
  const themeConstantsKey = `${typeOfUser.toLowerCase()}s` as keyof typeof ThemeConstants;
  const fillColor = ThemeConstants[themeConstantsKey]?.[type as never];

  const isVacations = type === PharmacistLegendVariant.vacation;
  const showRightBox = activeWebView === CalendarStateKey.month && !isVacations;

  const handleSelect = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      // What does this do?
      // onSelect({ start, end, title, type, id, code });
      e.stopPropagation();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [end, id, onSelect, start, title, type, code],
  );

  useEffect(() => {
    const refCurrent = ref.current;

    if (refCurrent) {
      refCurrent.addEventListener('click', handlePress);
    }

    return () => {
      if (refCurrent) {
        refCurrent.removeEventListener('click', handlePress);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StackedBox
      stackGap={1}
      stackHeight={3}
      showStacks={isLongTerm}
      backgroundColor={
        stackedSmallChipsColors[type as keyof typeof stackedChipsColors]
      }
    >
      <Box
        component="div"
        sx={classes.eventContainer(type, style, typeOfUser, isDeleted)}
        ref={ref}
        onMouseDown={stopMouseDown}
        onClick={handleSelect}
      >
        <CalendarTooltip
          onClick={handleTooltipClick}
          eventType={type}
          durations={durations ?? []}
          date={start}
        >
          <Grid container wrap="nowrap" sx={classes.gridContainer}>
            <Grid
              item
              md={showReplacementType ? 7 : 9}
              sx={[classes.titleContainer, classes.center]}
            >
              <Typography
                sx={classes.eventTitle(type, typeOfUser, !!isDeleted)}
              >
                {strings[type]}
              </Typography>

              {showBellIcon && (
                <Box sx={[classes.bellContainer, classes.center]}>
                  <BellIcon
                    height={18}
                    width={18}
                    fill={fillColor}
                    stroke={fillColor}
                  />
                </Box>
              )}
            </Grid>

            {isMatchingPrev && (
              <Grid sx={[genericClasses.center]} item>
                <WavingHand />
              </Grid>
            )}

            {isInWatchlistPrev && (
              <Grid sx={[genericClasses.center]} item>
                <Watchlisted inCalendar />
              </Grid>
            )}

            {showRightBox && (
              <Grid md={3} item sx={classes.rightGrid}>
                <Box sx={classes.rightBlock(typeOfUser)}>
                  <Typography
                    sx={classes.textColor(type, typeOfUser, !!isDeleted)}
                  >
                    {isDeleted ? '-' : count}
                  </Typography>
                </Box>
              </Grid>
            )}

            {showReplacementType && (
              <ProfessionalIconList
                replacementTypes={replacementTypes ?? []}
                type={eventType as PharmacyLegendVariant}
              />
            )}
          </Grid>
        </CalendarTooltip>
      </Box>
    </StackedBox>
  );
};
export default CustomMonthEvent;
